import { Box, Button, TextField } from "@mui/material";
import { ComponentProps } from "../types";
import { CSSProperties, useContext, useMemo, useState } from "react";
import DashboardPageContext from "../DashboardPageContext";
import ExecutionTypeContext, { Mode } from "../ExecutionTypeContext";
import DashboardEnvContext from "../DashboardEnvContext";

import Editor from "react-simple-code-editor";
import { highlight, languages, Grammar } from "prismjs";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/themes/prism.css"; //Example style, you can use another

import prettier from "prettier/standalone";
import prettierPluginEstree from "prettier/plugins/estree";
import prettierPluginBabel from "prettier/plugins/babel";

function Component({ component, evaluteScript }: ComponentProps) {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const dashboard = useContext(DashboardEnvContext);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { context, params, updateContext } = useContext(DashboardPageContext);
	const exeMode = useContext(ExecutionTypeContext);
	const [internalValue, setInternalValue] = useState<string | undefined>(component.properties["binding"] ? (evaluteScript(component.properties["binding"]) as string) : undefined);
	const [cachedValue, setCachedValue] = useState<string | undefined>(component.properties["binding"] ? (evaluteScript(component.properties["binding"]) as string) : undefined);

	const doBlur = (event: React.FocusEvent<HTMLDivElement, Element> | React.FocusEvent<HTMLTextAreaElement, Element>) => {
		if (cachedValue !== internalValue) {
			const script = component.properties["binding"] + "=" + (internalValue ? "`" + internalValue.replace(/`/g, "\\`").replace(/\$/g, "\\$") + "`" : "undefined");
			evaluteScript(script);
			updateContext(context);
		}
	};

	const style = useMemo(() => {
		const style = {} as CSSProperties;
		if (exeMode?.mode === Mode.DESIGN && component.id === exeMode?.activeComponent?.id) style.border = "1px dashed red";
		if (component.properties && component.properties["height"]) {
			style.height = component.properties["height"];
		}
		if (component.properties && component.properties["minHeight"]) style.minHeight = component.properties["minHeight"];
		if (component.properties && component.properties["maxHeight"]) style.maxHeight = component.properties["maxHeight"];
		if (component.properties["flex"]) style.flex = component.properties["flex"];
		return style;
	}, [component, exeMode]);

	const args = useMemo(() => {
		const args = {} as { [key: string]: unknown };
		if (component.properties["height"]) {
			args["height"] = component.properties["height"];
		}
		return args;
	}, [component, context, updateContext, evaluteScript]);

	const boxArgs = useMemo(() => {
		const args = {} as { [key: string]: unknown };
		if (exeMode?.mode === Mode.DESIGN && exeMode.locatorActivated === true) {
			args["onClick"] = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
				if (exeMode.updateActiveComponent) exeMode.updateActiveComponent(component);
				event.stopPropagation();
			};
		}
		return args;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [component, context]);

	return (
		<Box style={style} sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "stretch" }} {...boxArgs}>
			<Box sx={{ alignSelf: "stretch", background: "#f0f0f0", borderRadius: "4px" }}>
				<Button
					onClick={async () => {
						if (internalValue) {
							const formatted = await prettier.format(internalValue, {
								parser: "babel",
								plugins: [prettierPluginEstree, prettierPluginBabel],
								semi: true,
								singleQuote: true,
								tabWidth: 6,
								trailingComma: "all",
								printWidth: 120,
							});
							setInternalValue(formatted);
						}
					}}
				>
					Format
				</Button>
			</Box>
			<Box sx={{ flexGrow: 1, position: "relative" }}>
				<Box sx={{ position: "absolute", top: 0, right: 0, left: 0, bottom: 0, overflow: "auto" }}>
					<Editor
						value={internalValue || ""}
						highlight={(code) => highlight(code, languages.js, "js")}
						onValueChange={(code) => setInternalValue(code)}
						onBlur={doBlur}
						padding={10}
						tabSize={6}
						style={{
							fontFamily: '"Fira code", "Fira Mono", monospace',
							fontSize: 14,
							width: "1500px",
							minHeight: "100px",
							background: "#fafafa",
						}}
					/>
				</Box>
			</Box>
		</Box>
	);
}

export default Component;
